import React, { useEffect, useState } from 'react';
import classnames from 'classnames';
import { gikClassPrefix } from '@gik/core/constants';
import {
  Button,
  FormProps,
  Form,
  FormGroup,
  Input,
  TextArea,
  ValidationError,
  Hint,
  LoadingSpinner,
  ValidationErrors,
  HTMLParser,
} from '@gik/ui';
import Checkbox from '@gik/ui/Checkbox';
import { ShippingDetails } from '../../types';
import { formatCurrency } from '@gik/core/utils/format';
import { FormApi, FieldState } from 'final-form';
import { Field } from 'react-final-form';
import i18n from '@gik/i18n';
import { CartItem, TipModal } from '@gik/checkout';
import useOrderTaxCalculation from '../useOrderTaxCalculation';
import { RadioSelect } from '@gik/ui/Radio';
import { useUserStore } from '@gik/core/store/UserStore';

export interface OrderSummaryProps extends FormProps {
  /**
   * Render Shipping information in order summary
   */
  shipping?: boolean;

  /**
   * Allow a shipping option to be selected (standard, fast delivery)
   */
  hasShipping?: boolean;

  shippingDetails?: ShippingDetails;
  cart?: CartItem[];
  initialValues?: OrderSummaryValues;
  setIsOrderSummaryFormLoading?(value: boolean): void;
  onTipChange?(tipPercentage: number, tipAmount: number): void;
  errorMessage?: string;
  stripeErrorMessage?: string;
}

export interface OrderSummaryValues {
  agree?: boolean;
  tipPercentage?: number;
  tipAmount?: number;
  total?: number; // for verification against backend only
  messageToRecipient?: string;
  shippingOptionName?: string; // e.g. "Standard (5-7 business days)". id cannot be used as it is always "flat_rate"
  subscribeToNewsletter?: boolean;
}

export interface OrderSummaryErrors extends ValidationErrors {
  agree?: ValidationError;
  tipPercentage?: ValidationError;
  shippingOptionName?: ValidationError;
  messageToRecipient?: ValidationError;
}

const blockName = `${gikClassPrefix}-order-summary`;

export default function OrderSummary({
  className,
  cart,
  initialValues,
  shippingDetails,
  hasShipping,
  setIsOrderSummaryFormLoading,
  onChange,
  onTipChange,
  onSubmit,
  shipping,
  errorMessage,
  stripeErrorMessage,
  ...otherProps
}: OrderSummaryProps): React.ReactElement {
  let subtotal = 0;
  if (cart) {
    cart.forEach(order => {
      subtotal += order.price || 0;
    });
  }

  const checkoutType = cart[0].checkoutType;
  const calculateTaxes = hasShipping || checkoutType === 'gik-premium'; // TODO: other checkout types may also have tax

  const orderTaxCalulation = useOrderTaxCalculation(
    calculateTaxes,
    [].concat(
      ...cart.map(cartItem => (cartItem.groupedProductIds?.length ? cartItem.groupedProductIds : [cartItem.productId]))
    ),
    shippingDetails?.country,
    shippingDetails?.state,
    shippingDetails?.city,
    shippingDetails?.postalCode,
    shippingDetails?.address1
  );
  const [selectedShippingOptionName, setSelectedShippingOptionName] = useState(initialValues.shippingOptionName);
  const selectedShippingOption =
    hasShipping && orderTaxCalulation?.shipping.find(option => option.name === selectedShippingOptionName);
  const shippingRate = parseFloat(selectedShippingOption?.subtotal || '0');
  const shippingTax = parseFloat(selectedShippingOption?.subtotal_tax || '0');
  const productTaxes = parseFloat(orderTaxCalulation?.products.total.subtotal_tax || '0');
  const totalTax = shippingTax + productTaxes;
  const initialPercentage = initialValues.tipPercentage || 10;

  const [tipPercentage, setTipPercentage] = React.useState(initialPercentage);
  const [tipAmount, setTipAmount] = React.useState(initialValues.tipAmount);
  const [tipOpen, setTipOpen] = React.useState(false);

  function handleTipOpen() {
    setTipOpen(true);
  }

  function handleTipSave(percentage: number, amount: number) {
    setTipPercentage(percentage);
    setTipAmount(amount);
    setTipOpen(false);
    if (onTipChange) onTipChange(percentage, amount);
  }

  function handleTipInit(percentage: number, amount: number) {
    setTipPercentage(percentage);
    setTipAmount(amount);
  }

  function handleTipClose() {
    setTipOpen(false);
  }

  function handleOnChange(values: OrderSummaryValues): Promise<void> | void {
    values.tipPercentage = tipPercentage;
    values.tipAmount = tipAmount;
    setSelectedShippingOptionName(values.shippingOptionName);
    if (onChange) onChange(values);
  }

  function handleOnSubmit(values: OrderSummaryValues, form: FormApi<object, object>): Promise<void> | void {
    values.tipPercentage = tipPercentage;
    values.tipAmount = tipAmount;
    values.total = total;
    if (onSubmit) onSubmit(values, form);
  }

  // TODO: consider using a schema (See Manual.story.tsx) so we do not need to add custom validation
  function customValidateForm(values: OrderSummaryValues): OrderSummaryErrors {
    const errors: OrderSummaryErrors = {};
    if (!values.agree) {
      errors.agree = { message: i18n.t('validation.required') };
    }

    if (!values.messageToRecipient && checkoutType === 'giftbox') {
      errors.messageToRecipient = { message: i18n.t('validation.required') };
    }

    if (values.messageToRecipient && values.messageToRecipient.length > messageToRecipientMaxLength) {
      errors.messageToRecipient = { message: i18n.t('validation.maxLength', { count: messageToRecipientMaxLength }) };
    }

    if (!values.shippingOptionName && hasShipping) {
      errors.shippingOptionName = { message: i18n.t('validation.required') };
    }

    return errors;
  }

  const blockClasses = classnames([blockName, className || '']);

  const tipValue = Math.ceil((tipPercentage / 100) * subtotal);
  // calculate total to 2 decimal places (NB: also passed to backend for verification)
  const total = parseFloat((subtotal + shippingRate + totalTax + tipValue).toFixed(2));

  const isLoading = calculateTaxes && !orderTaxCalulation;
  const messageToRecipientMaxLength = checkoutType === 'giftbox' ? 220 : 1000;

  const userId = useUserStore(state => state.id);

  useEffect(() => {
    if (setIsOrderSummaryFormLoading) {
      setIsOrderSummaryFormLoading(isLoading);
    }
  }, [isLoading, setIsOrderSummaryFormLoading]);

  return isLoading ? (
    <LoadingSpinner center />
  ) : (
    <Form
      onSubmit={handleOnSubmit}
      onChange={handleOnChange}
      initialValues={initialValues}
      vertical
      validate={customValidateForm}
      validateOnBlur={false}
      className={blockClasses}
      {...otherProps}
      render={() => {
        return (
          <div className="gik-order-summary">
            {checkoutType !== 'gik-premium' && (
              <Field name="messageToRecipient">
                {({ input, meta: fieldState }) => {
                  return (
                    <FormGroup fieldState={fieldState as FieldState<string>} className="tw-mt-4">
                      <TextArea
                        {...input}
                        autoGrow
                        rows={4}
                        maxLength={messageToRecipientMaxLength}
                        placeholder={`Message to Recipient${
                          checkoutType !== 'giftbox' ? ' (optional)' : ' (will be included in box)'
                        }`}
                      />
                    </FormGroup>
                  );
                }}
              </Field>
            )}
            <span className="gik-form-header">Your Order</span>

            <ul className="gik-datalist">
              <li>
                <label>
                  <strong>Subtotal</strong>
                </label>
                <span>{formatCurrency(subtotal)}</span>
              </li>
              {shipping && shippingDetails && (
                <>
                  <li>
                    <div>
                      <label>
                        <strong>Shipping</strong>
                      </label>
                      <span>
                        {hasShipping && (
                          <>
                            <Field name="shippingOptionName" type="radio-select">
                              {({ input, meta: fieldState }) => {
                                return (
                                  <FormGroup fieldState={fieldState as FieldState<unknown>}>
                                    <RadioSelect
                                      {...input}
                                      variant="primary"
                                      schema={orderTaxCalulation.shipping.map(option => {
                                        // only display the text before the bracket in bold
                                        const bracketIndex = option.name.indexOf('(');
                                        return {
                                          label: `<strong>${option.name.substring(
                                            0,
                                            bracketIndex
                                          )}</strong><br/>${option.name.substring(bracketIndex)}: $${option.subtotal}`,
                                          value: option.name,
                                          variant: 'primary',
                                        };
                                      })}
                                    />
                                  </FormGroup>
                                );
                              }}
                            </Field>
                            <br className="xs" />
                          </>
                        )}
                        <address>
                          {shippingDetails.firstName} {shippingDetails.lastName} <br />
                          {shippingDetails.address1} <br />
                          {shippingDetails.address2} <br />
                          {shippingDetails.city}, {shippingDetails.state} {shippingDetails.postalCode} <br />
                        </address>
                      </span>
                    </div>
                  </li>
                </>
              )}
              <li>
                <label>
                  <strong>Taxes</strong>
                </label>
                <span>{formatCurrency(totalTax)}</span>
              </li>
              <li className="gik-datalist__muted">
                <label>
                  <strong>Give InKind Tip</strong>
                </label>
                <span>
                  {formatCurrency(tipValue)}&nbsp;
                  <Button variant="primary-link" onClick={handleTipOpen}>
                    change
                  </Button>
                  <Field name="tipPercentage">
                    {({ input, meta: fieldState }) => {
                      return (
                        <FormGroup fieldState={fieldState as FieldState<string>}>
                          <Input {...input} type="hidden" value={tipValue + ''} />
                        </FormGroup>
                      );
                    }}
                  </Field>
                  <TipModal
                    isOpen={tipOpen}
                    percentageValue={tipPercentage}
                    totalCheckoutPrice={subtotal}
                    onSave={handleTipSave}
                    onClose={handleTipClose}
                    onInit={handleTipInit}
                  />
                </span>
                <Hint className="gik-datalist__help">
                  Your support of Give InKind helps us remain free when it&apos;s needed most.
                </Hint>
              </li>
              <li className="gik-datalist__muted">
                <label>
                  <strong>Total</strong>
                </label>
                <strong>{formatCurrency(total)}</strong>
              </li>
            </ul>

            <br className="sm" />
            <Field name="agree" type="checkbox">
              {({ input, meta: fieldState }) => {
                return (
                  <FormGroup fieldState={fieldState as FieldState<boolean>}>
                    <Checkbox
                      {...input}
                      checked={input.value || input.checked}
                      variant="primary"
                      label={
                        <span>
                          I have read and agree to the website{' '}
                          <Button variant="primary-link" href={'/terms-service'} target="_blank" rel="noreferrer">
                            terms and conditions
                          </Button>
                          *
                        </span>
                      }
                    />
                  </FormGroup>
                );
              }}
            </Field>

            {!userId && (
              <>
                <br className="sm" />
                <Field name="subscribeToNewsletter" type="checkbox">
                  {({ input, meta: fieldState }) => {
                    return (
                      <FormGroup fieldState={fieldState as FieldState<boolean>}>
                        <Checkbox
                          {...input}
                          checked={input.value || input.checked}
                          variant="primary"
                          label={<span>Update me on additional ideas for helping, resources, and new features</span>}
                        />
                      </FormGroup>
                    );
                  }}
                </Field>
              </>
            )}

            {(errorMessage || stripeErrorMessage) && (
              <div className="gik-order-summary__error-container">
                {stripeErrorMessage && <div className="gik-form__error">{stripeErrorMessage}</div>}
                {errorMessage && (
                  <div className="gik-form__error">
                    <HTMLParser rawHtml={errorMessage} />
                  </div>
                )}
              </div>
            )}

            {checkoutType === 'tango-card' && (
              <Hint>
                <p className="gik-order-summary__disclaimer">
                  All Gift Card purchases are final and non-refundable.
                  <br />
                  Gift Cards are delivered 24 hours after purchase for security reasons.
                  <br />
                  Charges will appear as Give InKind.
                </p>
              </Hint>
            )}
          </div>
        );
      }}
    />
  );
}
